import { useState, useEffect } from 'react';
import {
  AlertCircle, Loader, Eye, EyeOff, Lock, Mail, User, ArrowRight, ArrowLeft,
  Sparkles, Check, Zap, Crown
} from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../components/auth/AuthContext';
import { useBrandIdentity } from '../../contexts/BrandIdentityContext';
import { authService } from '../../services/PlatformService';
import { stripeService } from '../../services/StripeService';
import EmbeddedCheckoutModal from '../../components/checkout/EmbeddedCheckoutModal';
import AddonSelector from '../../components/checkout/AddonSelector';

const UserRegistration = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser, login } = useAuth();
  const { colors: brandColors } = useBrandIdentity();

  const planFromUrl = searchParams.get('plan');
  const intervalFromUrl = searchParams.get('interval') || 'monthly';
  const [selectedPlan, setSelectedPlan] = useState(planFromUrl || null);
  const [selectedInterval, setSelectedInterval] = useState(intervalFromUrl);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [isRedirectingToCheckout, setIsRedirectingToCheckout] = useState(false);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);

  // Verificação de email
  const [step, setStep] = useState(1); // 1=form, 2=verification code
  const [verificationCode, setVerificationCode] = useState('');
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [codeCountdown, setCodeCountdown] = useState(0);

  // Normaliza erros da API. FastAPI/Pydantic v2 retorna detail como ARRAY de
  // objetos em 422 ({type, loc, msg, input, ctx}) — renderizar isso direto
  // quebra o React. Aqui convertemos para string legivel sem perder a info.
  const extractErrorMessage = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    if (!detail) return error?.message || fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map((d) => d?.msg || d?.message || JSON.stringify(d)).join('; ');
    }
    if (typeof detail === 'object') return detail.msg || detail.message || fallback;
    return String(detail);
  };

  const planInfo = {
    basic: {
      name: 'Básico',
      price: 39.90,
      icon: Zap,
      color: 'blue',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-300',
      textColor: 'text-blue-700'
    },
    pro: {
      name: 'Pro',
      price: 79.90,
      icon: Sparkles,
      color: 'amber',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-300',
      textColor: 'text-amber-700',
      popular: true
    },
    max: {
      name: 'Max',
      price: 149.90,
      icon: Crown,
      color: 'purple',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-300',
      textColor: 'text-purple-700'
    }
  };

  useEffect(() => {
    if (!planFromUrl) {
      navigate('/plans');
    }
  }, [planFromUrl, navigate]);

  useEffect(() => {
    // Não redireciona enquanto o checkout pós-cadastro está em andamento.
    if (currentUser && !isRedirectingToCheckout && !checkoutClientSecret) {
      navigate('/account');
    }
  }, [currentUser, navigate, isRedirectingToCheckout, checkoutClientSecret]);

  // Countdown timer para reenvio de código
  useEffect(() => {
    if (codeCountdown > 0) {
      const timer = setTimeout(() => setCodeCountdown(codeCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [codeCountdown]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (errors[name]) {
      setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
    }
    if (apiError) {
      setApiError('');
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Nome é obrigatório';
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
    }
    if (!formData.password) {
      newErrors.password = 'Senha é obrigatória';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Senha deve ter pelo menos 8 caracteres';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Senhas não coincidem';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Step 1: Enviar código de verificação
  const handleSendVerificationCode = async (e) => {
    e.preventDefault();
    if (isSendingCode) return;
    if (!validate()) return;

    setIsSendingCode(true);
    setApiError('');

    try {
      await authService.sendVerificationCode(formData.email, formData.name);
      setStep(2);
      setCodeCountdown(60);
    } catch (error) {
      setApiError(extractErrorMessage(error, 'Erro ao enviar código de verificação.'));
    } finally {
      setIsSendingCode(false);
    }
  };

  // Reenviar código
  const handleResendCode = async () => {
    if (codeCountdown > 0) return;
    setIsSendingCode(true);
    setApiError('');

    try {
      await authService.sendVerificationCode(formData.email, formData.name);
      setCodeCountdown(60);
      setVerificationCode('');
    } catch (error) {
      setApiError(extractErrorMessage(error, 'Erro ao reenviar código.'));
    } finally {
      setIsSendingCode(false);
    }
  };

  // Step 2: Verificar código e finalizar registro
  const handleVerifyAndRegister = async () => {
    if (verificationCode.length !== 6 || isSubmitting) return;

    setIsSubmitting(true);
    setApiError('');

    try {
      // 1. Verificar código
      const verifyResponse = await authService.verifyRegistrationCode(formData.email, verificationCode);
      const verificationToken = verifyResponse.data.verification_token;

      // 2. Criar conta com token de verificação
      const { confirmPassword, ...apiData } = formData;
      apiData.verification_token = verificationToken;
      const response = await authService.register(apiData);

      if (response && response.data) {
        // 3. Login automático
        login(response.data.user, response.data.token);

        // PLANO GRÁTIS: sem checkout/cartão — 2 créditos/mês já valem; entra direto
        if (selectedPlan === 'free') {
          navigate('/contents');
          return;
        }

        // 4. Abrir checkout embedded inline (modal)
        setIsRedirectingToCheckout(true);
        const plan = selectedPlan || 'basic';
        const checkoutResponse = await stripeService.createCheckoutSession(plan, selectedInterval, selectedAddons);

        if (checkoutResponse.success && checkoutResponse.data?.client_secret) {
          setCheckoutClientSecret(checkoutResponse.data.client_secret);
        } else {
          throw new Error('Não foi possível abrir o checkout. Acesse /plans para tentar novamente.');
        }
      } else {
        throw new Error('Resposta da API inválida após registro.');
      }
    } catch (error) {
      let errorMessage = 'Ocorreu um erro. Por favor, tente novamente.';
      errorMessage = extractErrorMessage(error, errorMessage);
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      setApiError(errorMessage);
      setIsRedirectingToCheckout(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDisplayPrice = (planId) => {
    const plan = stripeService.helpers.plans[planId];
    if (!plan) return 0;
    if (selectedInterval === 'yearly') {
      return plan.yearlyPrice / 12;
    }
    return plan.monthlyPrice;
  };

  // Tela de transição pós-cadastro — fica como fundo enquanto o modal do Stripe abre
  if (isRedirectingToCheckout) {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center py-12 px-4">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-amber-400 to-orange-500 rounded-3xl mb-6 animate-pulse">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Conta criada com sucesso!
            </h2>
            <p className="text-gray-600 mb-4">
              {checkoutClientSecret
                ? 'Finalize seu pagamento para ativar o plano.'
                : 'Abrindo checkout seguro...'}
            </p>
            {!checkoutClientSecret && (
              <Loader className="w-6 h-6 text-amber-500 animate-spin mx-auto" />
            )}
          </div>
        </div>

        {checkoutClientSecret && (
          <EmbeddedCheckoutModal
            clientSecret={checkoutClientSecret}
            onClose={() => {
              setCheckoutClientSecret(null);
              setIsRedirectingToCheckout(false);
              navigate('/account');
            }}
          />
        )}
      </>
    );
  }

  const currentPlan = selectedPlan ? planInfo[selectedPlan] : null;
  const PlanIcon = currentPlan?.icon || Sparkles;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex flex-col">

      {/* Top Bar - Voltar */}
      <div className="w-full px-4 pt-4">
        <div className="max-w-md mx-auto">
          {step === 1 ? (
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar
            </Link>
          ) : (
            <button
              onClick={() => { setStep(1); setApiError(''); setVerificationCode(''); }}
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar e corrigir dados
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center py-8 px-4">
        <div className="max-w-md w-full">

          {/* Logo e Header */}
          <div className="text-center mb-6">
            <Link to="/" className="inline-block mb-6">
              <img
                src="/logo.png"
                alt="hello, social"
                className="w-24 h-24 object-contain mx-auto"
              />
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {step === 1 ? 'Crie sua conta' : 'Verifique seu e-mail'}
            </h1>
            <p className="text-base text-gray-600">
              {step === 1
                ? 'Comece a criar conteúdos incríveis com IA'
                : <>Enviamos um código de 6 dígitos para <strong>{formData.email}</strong></>
              }
            </p>
          </div>

          {/* Badge do Plano Selecionado - apenas no step 1 */}
          {step === 1 && currentPlan && (
            <div className={`mb-6 p-4 rounded-2xl border-2 ${currentPlan.borderColor} ${currentPlan.bgColor}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-white">
                    <PlanIcon className={`w-5 h-5 ${currentPlan.textColor}`} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Plano selecionado</p>
                    <p className={`font-bold ${currentPlan.textColor}`}>
                      {currentPlan.name} - R$ {getDisplayPrice(selectedPlan).toFixed(2)}/mês
                      {selectedInterval === 'yearly' && (
                        <span className="text-xs font-normal ml-1 text-green-600">(anual)</span>
                      )}
                    </p>
                  </div>
                </div>
                <Link
                  to="/plans"
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  Alterar
                </Link>
              </div>
              {selectedInterval === 'yearly' && (
                <div className="mt-3 flex items-center gap-2 text-sm text-green-700">
                  <Check className="w-4 h-4" />
                  <span>
                    Plano anual — economia de {stripeService.helpers.getYearlyDiscount(selectedPlan)}%
                    ({stripeService.helpers.formatCurrency(stripeService.helpers.getYearlySavings(selectedPlan))}/ano)
                  </span>
                </div>
              )}
              {/* Créditos liberados imediatamente após a assinatura */}
              <div className={`${selectedInterval === 'yearly' ? 'mt-2' : 'mt-3'} flex items-center gap-2 text-sm text-green-700`}>
                <Check className="w-4 h-4" />
                <span>Créditos liberados na hora — cancele quando quiser.</span>
              </div>
              {currentPlan.popular && (
                <div className="mt-2 flex items-center gap-2 text-sm text-amber-700">
                  <Sparkles className="w-4 h-4" />
                  <span>Mais vendido!</span>
                </div>
              )}
            </div>
          )}

          {/* Add-ons selecionaveis (E-book + Consultoria) - apenas no step 1 */}
          {step === 1 && (
            <AddonSelector selected={selectedAddons} onChange={setSelectedAddons} />
          )}

          {/* Card de Cadastro */}
          <div className="bg-white border-2 border-gray-200 rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all">

            {/* Mostrar erro */}
            {apiError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-start mb-5">
                <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{apiError}</span>
              </div>
            )}

            {/* ===== STEP 1: Formulário de cadastro ===== */}
            {step === 1 && (
              <>
                <form className="space-y-4" onSubmit={handleSendVerificationCode}>
                  {/* Nome */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Nome completo
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                        <User className="w-4 h-4" />
                      </span>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`w-full pl-10 pr-4 py-3 text-sm border-2 ${
                          errors.name ? 'border-red-300' : 'border-gray-200'
                        } rounded-xl focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-500 transition-all`}
                        placeholder="João Silva"
                      />
                    </div>
                    {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      E-mail
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                        <Mail className="w-4 h-4" />
                      </span>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full pl-10 pr-4 py-3 text-sm border-2 ${
                          errors.email ? 'border-red-300' : 'border-gray-200'
                        } rounded-xl focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-500 transition-all`}
                        placeholder="seu@email.com"
                      />
                    </div>
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>

                  {/* Senha */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Senha
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                        <Lock className="w-4 h-4" />
                      </span>
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className={`w-full pl-10 pr-10 py-3 text-sm border-2 ${
                          errors.password ? 'border-red-300' : 'border-gray-200'
                        } rounded-xl focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-500 transition-all`}
                        placeholder="Mínimo 8 caracteres"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                  </div>

                  {/* Confirmar Senha */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Confirme a senha
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                        <Lock className="w-4 h-4" />
                      </span>
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className={`w-full pl-10 pr-10 py-3 text-sm border-2 ${
                          errors.confirmPassword ? 'border-red-300' : 'border-gray-200'
                        } rounded-xl focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-500 transition-all`}
                        placeholder="Digite a senha novamente"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
                  </div>

                  {/* Botão de Cadastro */}
                  <div className="pt-2">
                    <button
                      type="submit"
                      className="w-full py-3 px-4 bg-gradient-to-r from-amber-400 to-orange-500 rounded-xl text-white font-semibold hover:from-amber-500 hover:to-orange-600 transition-all transform hover:scale-[1.02] shadow-lg focus:outline-none focus:ring-4 focus:ring-amber-100 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isSendingCode}
                    >
                      {isSendingCode ? (
                        <>
                          <Loader className="w-5 h-5 mr-2 animate-spin" />
                          Enviando código...
                        </>
                      ) : (
                        <>
                          Continuar
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </button>
                  </div>
                </form>

                {/* Termos */}
                <p className="mt-4 text-xs text-gray-500 text-center">
                  Ao criar sua conta, você concorda com nossos{' '}
                  <Link to="/terms" className="text-amber-600 hover:underline">Termos de Serviço</Link>
                  {' '}e{' '}
                  <Link to="/privacy" className="text-amber-600 hover:underline">Política de Privacidade</Link>
                </p>

                {/* Link para login */}
                <div className="mt-6 text-center pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-600">
                    Já tem uma conta?{' '}
                    <Link
                      to="/login"
                      className="font-semibold text-amber-600 hover:text-amber-700 transition-colors hover:underline"
                    >
                      Fazer login
                    </Link>
                  </p>
                </div>
              </>
            )}

            {/* ===== STEP 2: Verificação de código ===== */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-amber-50 rounded-2xl mb-4">
                    <Mail className="w-7 h-7 text-amber-500" />
                  </div>
                  <p className="text-sm text-gray-600">
                    Digite o código de 6 dígitos que enviamos para o seu e-mail
                  </p>
                </div>

                {/* Input do código */}
                <div>
                  <input
                    type="text"
                    maxLength={6}
                    value={verificationCode}
                    onChange={(e) => {
                      setVerificationCode(e.target.value.replace(/\D/g, ''));
                      if (apiError) setApiError('');
                    }}
                    className="w-full text-center text-2xl tracking-[0.5em] py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-500 transition-all font-mono"
                    placeholder="000000"
                    autoFocus
                  />
                </div>

                {/* Botão verificar */}
                <button
                  onClick={handleVerifyAndRegister}
                  disabled={verificationCode.length !== 6 || isSubmitting}
                  className="w-full py-3 px-4 bg-gradient-to-r from-amber-400 to-orange-500 rounded-xl text-white font-semibold hover:from-amber-500 hover:to-orange-600 transition-all transform hover:scale-[1.02] shadow-lg focus:outline-none focus:ring-4 focus:ring-amber-100 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="w-5 h-5 mr-2 animate-spin" />
                      Verificando e criando conta...
                    </>
                  ) : (
                    <>
                      Verificar e criar conta
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </button>

                {/* Reenviar código */}
                <div className="text-center text-sm">
                  {codeCountdown > 0 ? (
                    <span className="text-gray-500">
                      Reenviar código em <strong>{codeCountdown}s</strong>
                    </span>
                  ) : (
                    <button
                      onClick={handleResendCode}
                      disabled={isSendingCode}
                      className="text-amber-600 hover:text-amber-700 font-medium hover:underline transition-colors"
                    >
                      {isSendingCode ? 'Enviando...' : 'Reenviar código'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer com segurança */}
          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-3 px-4 py-2 bg-white rounded-full border border-gray-200 text-xs text-gray-600 shadow-sm">
              <span className="text-green-500">🔒</span>
              <span>Pagamento seguro via Stripe</span>
              <span className="text-gray-300">•</span>
              <span>SSL 256-bit</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserRegistration;
